import React, { useState } from "react";
import { useAuth } from "./AuthContext";
import { Music, LogIn } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login({ onForgot }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fillDemo = (role) => {
    if (role === "admin") {
      setEmail("admin@melodyteacher.com");
      setPassword("admin123");
    } else {
      setEmail("teacher@melodyteacher.com");
      setPassword("teacher123");
    }
    setError("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}!`);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6 font-sans">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-indigo-600 to-violet-500 flex items-center justify-center text-white shadow-lg shadow-indigo-200">
            <Music className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold tracking-tight text-slate-900 leading-tight">Melody Teacher</h1>
            <p className="text-xs text-slate-500">Music Class Manager</p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-2xl shadow-xl p-8 space-y-5" data-testid="login-form">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Sign in to your dashboard</h2>
            <p className="text-xs text-slate-500 mt-1">Manage attendance, fees, and practice reminders for every student in your studio.</p>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 block">Email</label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@melodyteacher.com" className="h-11 border-slate-200" data-testid="login-email-input" autoComplete="email" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Password</label>
              <button type="button" onClick={onForgot} className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-700" data-testid="forgot-password-link">
                Forgot password?
              </button>
            </div>
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" className="h-11 border-slate-200" data-testid="login-password-input" autoComplete="current-password" />
          </div>

          {error && (
            <div className="text-xs font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <Button type="submit" disabled={loading} className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-bold gap-2" data-testid="login-submit-btn">
            <LogIn className="w-4 h-4" />
            {loading ? "Signing in…" : "Sign In"}
          </Button>

          <div className="pt-4 border-t border-slate-100 space-y-2">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Demo accounts — click to fill</p>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => fillDemo("admin")}
                className="text-left rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 px-3 py-2 transition"
                data-testid="fill-admin-demo-btn">
                <p className="text-xs font-bold text-slate-900">Admin</p>
                <p className="text-[10px] text-slate-500 truncate">admin@melodyteacher.com</p>
              </button>
              <button type="button" onClick={() => fillDemo("teacher")}
                className="text-left rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 px-3 py-2 transition"
                data-testid="fill-teacher-demo-btn">
                <p className="text-xs font-bold text-slate-900">Teacher</p>
                <p className="text-[10px] text-slate-500 truncate">teacher@melodyteacher.com</p>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
